import './styles/EventBlastTable.scss';
import { AppContext } from '../../Context.tsx';
import React, { useContext, useEffect, useState, useRef } from "react";
import { Button, Flex, Table } from "antd";
import type { TableColumnsType, TableProps, TableColumnType } from "antd";
import EventController from "../../pages/Admin/Controller/event_controller.ts";
import InfinityEncryptor from "../../components/EncryptDecrypt/InfinityEncryptor";
import { notification, InputRef, Input, Space, Modal } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import axios from "axios";

//i-components
import Checker from '../../i-components/Checker.tsx';

//controllers
import DatabaseInterfaceController from '../../i-controllers/DatabaseInterfaceController.tsx';

//classes
import IFile from "../../i-classes/ifile.tsx";

//nonstate
const nonstate = {
  waitForAttachmentUploadInterval: 1000
}

type TableRowSelection<T extends object = object> =
  TableProps<T>["rowSelection"];

interface DataType {
  key: React.Key;
  name: string;
  email: string;
  member_type: string;
  event_category_name: string;
  sent_email_userID: string;
  userID: string;
  state: string;
  mall_location: string;
}

type DataIndex = keyof DataType;

const App = ({ emailContent, emailSubject, attachments, emailTemplate, event_categoryID, footerSignature }) => {
  const [modal, contextHolderModal] = Modal.useModal();
  const confirm = (message, onConfirm, onCancel) => {
    modal.confirm({
      title: "Warning!",
      icon: <ExclamationCircleOutlined />,
      content: message,
      okText: "Ok",
      cancelText: "Cancel",
      onOk: () => {
        onConfirm();
      },
      onCancel: () => {
        onCancel();
      },
    });
  };

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, desc, msg) => {
    api[type]({
      message: msg,
      description: desc,
    });
  };

  //parameters declaration
  const key = "12345";
  /* key has no length limit, the longer the length more exhaustive encryption/decryption to process. Key shall not have 0, any 0's will be autoremoved */

  const mode = "YMDHI"; //optional parameter, default will be 'YMDHI'

  //initialize encyptor
  const Encryptor = new InfinityEncryptor(key, mode);
  const [rows, setRows] = useState<DataType[]>([]);
  const [membershipTypeFilter, setMembershipTypeFilter] = useState([]);
  const [shouldRefetchData, setShouldRefetchData] = useState(false);
  const [allRows, setAllRows] = useState<DataType[]>([]); // Store all data
  const userID = localStorage.getItem("userID");

  //added by delon on 2025-09-29
  const [shouldAddAndroidLinkFooter, SetShouldAddAndroidLinkFooter] = useState(true);
  const [shouldAddIosLinkFooter, SetShouldAddIosLinkFooter] = useState(true);

  useEffect(() => {
    if (Number(event_categoryID) === 5) {
      EventController.getCommitteeRecipients().then((data) => {
        const row = data?.map((item: any, index: number) => {
          return {
            key: index,
            name: item.name,
            email: item.email,
            member_type: item.member_type_label,
            email_sent: item.email_sent,
            userID: item.userID,
            state: item.registered_address_state,
            mall_location: item.mall_location
          };
        });

        setRows(row);
      });
    } else {
      EventController.getEventEmailRecipient().then((data) => {
        const row = data?.map((item: any, index: number) => {
          return {
            key: index,
            name: item.name,
            email: item.email,
            member_type: item.member_type_label,
            email_sent: item.email_sent,
            userID: item.userID,
            state: item.registered_address_state,
            mall_location: item.mall_location
          };
        });

        setRows(row);
        setAllRows(row); // Store all rows for selection
      });
    }

    EventController.getMemberType().then((response) => {
      const filters = response?.map((item) => ({
        text: item.label,
        value: item.label,
      }));

      setMembershipTypeFilter(filters);
    });
  }, [shouldRefetchData, event_categoryID, emailContent, emailSubject]);

  // **************************** SEARCH FILTER ANTD TABLE *****************************************************
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
          .toString()
          .toLowerCase()
          .includes((value as string).toLowerCase())
        : false,
    onFilterDropdownOpenChange: (open) => {
      if (open) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });
  // ************************************************************************************************************

  const stateOptions = [
    {
      text: "Johor",
      value: "Johor",
    },
    {
      text: "Kedah",
      value: "Kedah",
    },
    {
      text: "Kelantan",
      value: "Kelantan",
    },
    {
      text: "Malacca",
      value: "Malacca",
    },
    {
      text: "Negeri Sembilan",
      value: "Negeri Sembilan",
    },
    {
      text: "Overseas",
      value: "Overseas",
    },
    {
      text: "Pahang",
      value: "Pahang",
    },
    {
      text: "Penang",
      value: "Penang",
    },
    {
      text: "Perak",
      value: "Perak",
    },
    {
      text: "Sabah",
      value: "Sabah",
    },
    {
      text: "Sarawak",
      value: "Sarawak",
    },
    {
      text: "Selangor",
      value: "Selangor",
    },
    {
      text: "Terengganu",
      value: "Terengganu",
    },
    {
      text: "WP-KL",
      value: "WP-KL",
    },
    {
      text: "WP-Labuan",
      value: "WP-Labuan",
    },
    {
      text: "WP-Putrajaya",
      value: "WP-Putrajaya",
    },
  ];
  const columns: TableColumnsType<DataType> = [
    { title: "Name", dataIndex: "name", ...getColumnSearchProps("name") },
    { title: "Email", dataIndex: "email", ...getColumnSearchProps("email") },
    {
      title: "Membership Type",
      dataIndex: "member_type",
      filters: membershipTypeFilter,
      onFilter: (value, record) => record.member_type === value,
      render: (text: any) => (
        <div style={{ whiteSpace: "normal" }}>
          {/* This allows for multi-line text */}
          {text}
        </div>
      ),
    },
    {
      title: "State",
      dataIndex: "state",
      filters: stateOptions,
      onFilter: (value, record) => record.state === value,
      render: (data: any) => {
        return (
          <div style={{ whiteSpace: "normal" }}>
            {/* This allows for multi-line text */}
            {data}
          </div>
        );
      },
    },
    {
      title: "Mall Location",
      dataIndex: "mall_location",
      filters: stateOptions,
      onFilter: (value, record) => record.mall_location === value,
      render: (data: any) => {
        return (
          <div style={{ whiteSpace: "normal" }}>
            {/* This allows for multi-line text */}
            {data}
          </div>
        );
      },
    },
  ];

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(false);

  const [fileName, setFileName] = useState("");

  //edited by delon 2025-05-28
  const attachmentUrlsRef = useRef<string[]>([]);
  const waitForAttachmentUploadFinishIntervalRef = useRef<any>(null);

  useEffect(() => {
    return (() => {
      if (waitForAttachmentUploadFinishIntervalRef.current) {
        clearInterval(waitForAttachmentUploadFinishIntervalRef.current);
        waitForAttachmentUploadFinishIntervalRef.current = null;
      }
    })
  }, []);

  // useEffect(() => {
  //   if (attachment) {
  //     console.log(attachment);
  //     setFileName(attachment.name);

  //     const reader = new FileReader();
  //     reader.readAsDataURL(attachment); // Read file as Base64

  //     reader.onload = () => {
  //       const result = reader.result as string; // Type assertion (we know it's a string after onload)
  //       const value = result.split(",")[1]; // Extract Base64

  //       // Save base64 content in state or send to API
  //       setBase64Content(value);
  //     };

  //     reader.onerror = (error) => {
  //       console.error("Error reading file:", error);
  //     };
  //   } else {
  //     setBase64Content("");

  //     setFileName("");
  //   }
  // }, [attachment]); --deprecated to new implements

  const appContext = useContext(AppContext);
  const database = new DatabaseInterfaceController(appContext);

  const handleClickBlast = async (userID: string) => {
    setLoading(true);
    //edited by delon 2025-05-28
    // //update footerSignature
    database.Queue('updateFooterSignature', [
      ['updateFooterSignature', 'true'],
      ['footerSignature', footerSignature]
    ]);

    //before blast, prepare attachment links
    //upload attachments to server, get links

    attachmentUrlsRef.current = [];
    //check for attachments exsted on server
    attachments.map((attachment: IFile) => {
      if(attachment.base64.includes('https://'))
        attachmentUrlsRef.current.push(attachment.base64);
    });

    const IEncryptor = new InfinityEncryptor('22499', 'MYDIH');
    const blastID = new Date().getTime().toString();

    attachments.map((attachment: IFile) => {
      //check if attachment not already exist on server
      if(!attachment.base64.includes('https://')) {
        // const base64s = ChunkBase64(attachment.base64);

        const formData = new FormData();
        formData.append('s3cr3t', IEncryptor.Encrypt('ppkm_14082024_mobile'));
        formData.append('uploadEBlastAttachment', IEncryptor.Encrypt('true'));
        formData.append('blastID', IEncryptor.Encrypt(blastID));
        formData.append('fileName', IEncryptor.Encrypt(attachment.GetFileName()));
        formData.append('base64', 'NOENCRYPT' + attachment.base64);

        axios.post('https://www.ppkm.org.my/mobile_backend/scripts/eblastControl.php', formData, { withCredentials: false }).then((res: any) => {
          if (!res.data.includes('_false')) {
            //file upload success
            attachmentUrlsRef.current.push(res.data);
            console.log('file uploaded successful!: ');
          } else {
            console.log('fileUploadErr: ' + res);
            alert('Attachment upload failed, please try again.');
            attachmentUrlsRef.current = [];

            if (waitForAttachmentUploadFinishIntervalRef.current != null) {
              clearInterval(waitForAttachmentUploadFinishIntervalRef.current);
              waitForAttachmentUploadFinishIntervalRef.current = null;
            }
          }
        }).catch((err: string) => {
          //check file upload size
          const base64SizeKB = new Blob([attachment.base64]).size / 1024;
          console.log(`checkAttachmentSize: ${base64SizeKB.toFixed(2)} KB`);

          console.log('err: ' + err);
          alert('Attachment upload failed, please try again.');
          attachmentUrlsRef.current = [];

          if (waitForAttachmentUploadFinishIntervalRef.current != null) {
            clearInterval(waitForAttachmentUploadFinishIntervalRef.current);
            waitForAttachmentUploadFinishIntervalRef.current = null;
          }
        });
      }
    });

    if (waitForAttachmentUploadFinishIntervalRef.current != null) {
      clearInterval(waitForAttachmentUploadFinishIntervalRef.current);
    }

    waitForAttachmentUploadFinishIntervalRef.current = setInterval(async () => {
      if (attachments.length == attachmentUrlsRef.current.length) {
        //clear interval
        clearInterval(waitForAttachmentUploadFinishIntervalRef.current);
        waitForAttachmentUploadFinishIntervalRef.current = null;

        console.log('email blasting!');

        //all attachmets uploaded complete, proceed to blast email
        // Temporary array to collect and deduplicate emails
        const emailSet = new Set<string>();
        // const emailSet =
        //   "test001@example.com,test002@example.com,test003@example.com,test004@example.com,test005@example.com,test006@example.com,test007@example.com,test008@example.com,test009@example.com,test010@example.com,test011@example.com,test012@example.com,test013@example.com,test014@example.com,test015@example.com,test016@example.com,test017@example.com,test018@example.com,test019@example.com,test020@example.com,test021@example.com,test022@example.com,test023@example.com,test024@example.com,test025@example.com,test026@example.com,test027@example.com,test028@example.com,test029@example.com,test030@example.com,test031@example.com,test032@example.com,test033@example.com,test034@example.com,test035@example.com,test036@example.com,test037@example.com,test038@example.com,test039@example.com,test040@example.com,test041@example.com,test042@example.com,test043@example.com,test044@example.com,test045@example.com,test046@example.com,test047@example.com,test048@example.com,test049@example.com,test050@example.com,test051@example.com,test052@example.com,test053@example.com,test054@example.com,test055@example.com,test056@example.com,test057@example.com,test058@example.com,test059@example.com,test060@example.com,test061@example.com,test062@example.com,test063@example.com,test064@example.com,test065@example.com,test066@example.com,test067@example.com,test068@example.com,test069@example.com,test070@example.com,test071@example.com,test072@example.com,test073@example.com,test074@example.com,test075@example.com,test076@example.com,test077@example.com,test078@example.com,test079@example.com,test080@example.com,test081@example.com,test082@example.com,test083@example.com,test084@example.com,test085@example.com,test086@example.com,test087@example.com,test088@example.com,test089@example.com,test090@example.com,test091@example.com,test092@example.com,test093@example.com,test094@example.com,test095@example.com,test096@example.com,test097@example.com,test098@example.com,test099@example.com,test100@example.com,test101@example.com,test102@example.com,test103@example.com,test104@example.com,test105@example.com,test106@example.com,test107@example.com,test108@example.com,test109@example.com,test110@example.com,test111@example.com,test112@example.com,test113@example.com,test114@example.com,test115@example.com,test116@example.com,test117@example.com,test118@example.com,test119@example.com,test120@example.com,test121@example.com,test122@example.com,test123@example.com,test124@example.com,test125@example.com,test126@example.com,test127@example.com,test128@example.com,test129@example.com,test130@example.com,test131@example.com,test132@example.com,test133@example.com,test134@example.com,test135@example.com,test136@example.com,test137@example.com,test138@example.com,test139@example.com,test140@example.com,test141@example.com,test142@example.com,test143@example.com,test144@example.com,test145@example.com,test146@example.com,test147@example.com,test148@example.com,test149@example.com,test150@example.com,test151@example.com,test152@example.com,test153@example.com,test154@example.com,test155@example.com,test156@example.com,test157@example.com,test158@example.com,test159@example.com,test160@example.com,test161@example.com,test162@example.com,test163@example.com,test164@example.com,test165@example.com,test166@example.com,test167@example.com,test168@example.com,test169@example.com,test170@example.com,test171@example.com,test172@example.com,test173@example.com,test174@example.com,test175@example.com,test176@example.com,test177@example.com,test178@example.com,test179@example.com,test180@example.com,test181@example.com,test182@example.com,test183@example.com,test184@example.com,test185@example.com,test186@example.com,test187@example.com,test188@example.com,test189@example.com,test190@example.com,test191@example.com,test192@example.com,test193@example.com,test194@example.com,test195@example.com,test196@example.com,test197@example.com,test198@example.com,test199@example.com,test200@example.com,test201@example.com,test202@example.com,test203@example.com,test204@example.com,test205@example.com,test206@example.com,test207@example.com,test208@example.com,test209@example.com,test210@example.com,test211@example.com,test212@example.com,test213@example.com,test214@example.com,test215@example.com,test216@example.com,test217@example.com,test218@example.com,test219@example.com,test220@example.com,test221@example.com,test222@example.com,test223@example.com,test224@example.com,test225@example.com,test226@example.com,test227@example.com,test228@example.com,test229@example.com,test230@example.com,test231@example.com,test232@example.com,test233@example.com,test234@example.com,test235@example.com,test236@example.com,test237@example.com,test238@example.com,test239@example.com,test240@example.com,test241@example.com,test242@example.com,test243@example.com,test244@example.com,test245@example.com,test246@example.com,test247@example.com,test248@example.com,test249@example.com,test250@example.com,test251@example.com,test252@example.com,test253@example.com,test254@example.com,test255@example.com,test256@example.com,test257@example.com,test258@example.com,test259@example.com,test260@example.com,test261@example.com,test262@example.com,test263@example.com,test264@example.com,test265@example.com,test266@example.com,test267@example.com,test268@example.com,test269@example.com,test270@example.com,test271@example.com,test272@example.com,test273@example.com,test274@example.com,test275@example.com,test276@example.com,test277@example.com,test278@example.com,test279@example.com,test280@example.com,test281@example.com,test282@example.com,test283@example.com,test284@example.com,test285@example.com,test286@example.com,test287@example.com,test288@example.com,test289@example.com,test290@example.com,test291@example.com,test292@example.com,test293@example.com,test294@example.com,test295@example.com,test296@example.com,test297@example.com,test298@example.com,test299@example.com,test300@example.com,test301@example.com,test302@example.com,test303@example.com,test304@example.com,test305@example.com,test306@example.com,test307@example.com,test308@example.com,test309@example.com,test310@example.com,test311@example.com,test312@example.com,test313@example.com,test314@example.com,test315@example.com,test316@example.com,test317@example.com,test318@example.com,test319@example.com,test320@example.com,test321@example.com,test322@example.com,test323@example.com,test324@example.com,test325@example.com,test326@example.com,test327@example.com,test328@example.com,test329@example.com,test330@example.com,test331@example.com,test332@example.com,test333@example.com,test334@example.com,test335@example.com,test336@example.com,test337@example.com,test338@example.com,test339@example.com,test340@example.com,test341@example.com,test342@example.com,test343@example.com,test344@example.com,test345@example.com,test346@example.com,test347@example.com,test348@example.com,test349@example.com,test350@example.com,test351@example.com,test352@example.com,test353@example.com,test354@example.com,test355@example.com,test356@example.com,test357@example.com,test358@example.com,test359@example.com,test360@example.com,test361@example.com,test362@example.com,test363@example.com,test364@example.com,test365@example.com,test366@example.com,test367@example.com,test368@example.com,test369@example.com,test370@example.com,test371@example.com,test372@example.com,test373@example.com,test374@example.com,test375@example.com,test376@example.com,test377@example.com,test378@example.com,test379@example.com,test380@example.com,test381@example.com,test382@example.com,test383@example.com,test384@example.com,test385@example.com,test386@example.com,test387@example.com,test388@example.com,test389@example.com,test390@example.com,test391@example.com,test392@example.com,test393@example.com,test394@example.com,test395@example.com,test396@example.com,test397@example.com,test398@example.com,test399@example.com,test400@example.com,test401@example.com,test402@example.com,test403@example.com,test404@example.com,test405@example.com,test406@example.com,test407@example.com,test408@example.com,test409@example.com,test410@example.com,test411@example.com,test412@example.com,test413@example.com,test414@example.com,test415@example.com,test416@example.com,test417@example.com,test418@example.com,test419@example.com,test420@example.com,test421@example.com,test422@example.com,test423@example.com,test424@example.com,test425@example.com,test426@example.com,test427@example.com,test428@example.com,test429@example.com,test430@example.com,test431@example.com,test432@example.com,test433@example.com,test434@example.com,test435@example.com,test436@example.com,test437@example.com,test438@example.com,test439@example.com,test440@example.com,test441@example.com,test442@example.com,test443@example.com,test444@example.com,test445@example.com,test446@example.com,test447@example.com,test448@example.com,test449@example.com,test450@example.com,test451@example.com,test452@example.com,test453@example.com,test454@example.com,test455@example.com,test456@example.com,test457@example.com,test458@example.com,test459@example.com,test460@example.com,test461@example.com,test462@example.com,test463@example.com,test464@example.com,test465@example.com,test466@example.com,test467@example.com,test468@example.com,test469@example.com,test470@example.com,test471@example.com,test472@example.com,test473@example.com,test474@example.com,test475@example.com,test476@example.com,test477@example.com,test478@example.com,test479@example.com,test480@example.com,test481@example.com,test482@example.com,test483@example.com,test484@example.com,test485@example.com,test486@example.com,test487@example.com,test488@example.com,test489@example.com,test490@example.com,test491@example.com,test492@example.com,test493@example.com,test494@example.com,test495@example.com,test496@example.com,test497@example.com,test498@example.com,test499@example.com,test500@example.com,test501@example.com,test502@example.com,test503@example.com,test504@example.com,test505@example.com,test506@example.com,test507@example.com,test508@example.com,test509@example.com,test510@example.com,test511@example.com,test512@example.com,test513@example.com,test514@example.com,test515@example.com,test516@example.com,test517@example.com,test518@example.com,test519@example.com,test520@example.com,test521@example.com,test522@example.com,test523@example.com,test524@example.com,test525@example.com,test526@example.com,test527@example.com,test528@example.com,test529@example.com,test530@example.com,test531@example.com,test532@example.com,test533@example.com,test534@example.com,test535@example.com,test536@example.com,test537@example.com,test538@example.com,test539@example.com,test540@example.com,test541@example.com,test542@example.com,test543@example.com,test544@example.com,test545@example.com,test546@example.com,test547@example.com,test548@example.com,test549@example.com,test550@example.com,test551@example.com,test552@example.com,test553@example.com,test554@example.com,test555@example.com,test556@example.com,test557@example.com,test558@example.com,test559@example.com,test560@example.com,test561@example.com,test562@example.com,test563@example.com,test564@example.com,test565@example.com,test566@example.com,test567@example.com,test568@example.com,test569@example.com,test570@example.com,test571@example.com,test572@example.com,test573@example.com,test574@example.com,test575@example.com,test576@example.com,test577@example.com,test578@example.com,test579@example.com,test580@example.com,test581@example.com,test582@example.com,test583@example.com,test584@example.com,test585@example.com,test586@example.com,test587@example.com,test588@example.com,test589@example.com,test590@example.com,test591@example.com,test592@example.com,test593@example.com,test594@example.com,test595@example.com,test596@example.com,test597@example.com,test598@example.com,test599@example.com,test600@example.com";

        selectedRows.forEach((item) => {
          if (item.email !== null) {
            emailSet.add(item.email);
          }
        });

        const member_userIDs: string[] = [];
        selectedRows.forEach((item) => {
          member_userIDs.push(item.userID);
        });

        const uniqueEmails = Array.from(emailSet); // Convert Set to Array
        const emailString = uniqueEmails.join(","); // Convert Array to String
        // const uniqueEmails = emailSet.split(",");

        //add ppkm logo to blasted email content
        const logo_element = '<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td align="center" valign="middle" style="height:auto;"><img id="logo-img" src="https://www.ppkm.org.my/ppkm/ppkmLogo.jpg" width="200" height="200" alt="Centered Image" style="display:block;margin:0 auto;"></td></tr></table>';

        let updatedEmailContent = '<!-- SECTION_START -->' + logo_element + '<!-- SECTION_END -->';
        
        updatedEmailContent += '<!-- SECTION_START -->' + emailContent + '<!-- SECTION_END -->';

        // Find all Base64 images
        const base64Images = emailContent.match(
          /<img src="data:image\/[^;]+;base64,[^"]+"/g
        );

        if (base64Images && base64Images.length > 0) {
          console.log(`Found ${base64Images.length} images, uploading...`);

          // Upload images and replace Base64 with URLs
          const uploadPromises = base64Images.map(async (base64Img) => {
            const imageUrl = await EventController.uploadImage(base64Img);
            if (imageUrl) {
              emailContent = emailContent.replace(
                base64Img,
                `<img src="${imageUrl}"`
              );
            }
          });

          await Promise.all(uploadPromises);
        }

        // //add footer signature
        updatedEmailContent += '<!-- SECTION_START -->' + footerSignature + '<!-- SECTION_END -->';

        // //add app links
        updatedEmailContent += '<!-- SECTION_START -->';
        if(shouldAddAndroidLinkFooter || shouldAddIosLinkFooter) {
          updatedEmailContent += '<p>';
          if(shouldAddAndroidLinkFooter) {
            updatedEmailContent += '<a href="https://play.google.com/store/apps/details?id=com.neudimenxion.PPKM_SuperApp"><img src="https://www.ppkm.org.my/mobile_backend/assets/icons/googleAppLinkBadge.jpeg" style="width: 150px; height: 50px; margin: 5px;"/></a>';
          }
          if(shouldAddIosLinkFooter) {
            updatedEmailContent += '<a href="https://apps.apple.com/bh/app/ppkm-super-app/id6736516881"><img src="https://www.ppkm.org.my/mobile_backend/assets/icons/iosAppLinkBadge.jpeg" style="width: 150px; height: 50px; margin: 5px;"/></a>';
          }
          updatedEmailContent += '</p>';
        }
        updatedEmailContent += '<!-- SECTION_END -->';

        // Prepare form data
        // const encryptedContent = Encryptor.Encrypt(emailContent);
        // const encryptedEmails = Encryptor.Encrypt(uniqueEmails);
        // console.log("Encrypted Emails Length:", encryptedEmails.length);
        // console.log("Unique Emails Length:", uniqueEmails.length);

        const formData = new FormData();
        formData.append("recipient_emails", emailString);
        // console.log(emailSet);
        // const encryptedContent = Encryptor.Encrypt(emailContent);
        formData.append("email_content", Encryptor.Encrypt(updatedEmailContent));
        formData.append("email_subject", Encryptor.Encrypt(emailSubject));
        // formData.append('email_template', Encryptor.Encrypt(emailTemplate));
        // formData.append("file_name", Encryptor.Encrypt(fileName)); ---deprecated
        // formData.append("base64Content", base64Content);
        formData.append("member_userID", Encryptor.Encrypt(member_userIDs));
        formData.append('attachment_urls', Encryptor.Encrypt(attachmentUrlsRef.current.length > 0 ? attachmentUrlsRef.current.join(',') : ''));
        // formData.append("attachment", attachment); --deprecated

        formData.append("userID", userID);

        // console.log(uniqueEmails);
        // console.log(updatedEmailContent);
        // // console.log(base64Content);
        // console.log('checksum: ' + emailTemplate);
        if (emailContent && emailSubject) {
          // Use the deduplicated emails directly for the API call
          EventController.blastEvent(formData).then((response: any) => {
            // console.log(response);
            if (response.status === "success") {
              setLoading(false);

              openNotificationWithIcon(
                "success",
                "The circular has been sent to all recipients.",
                "Success"
              );

              setShouldRefetchData((prev) => !prev); // Toggle to re-trigger useEffect
              setSelectedRowKeys([]); // Clear all selections to uncheck checkboxes
            } else {
            }
          });
        } else {
          if (!emailContent) {
            confirm(
              "Email content must not be empty!",
              () => {
                setLoading(false);
              },
              () => {
                setLoading(false);
              }
            );
          } else if (!emailSubject) {
            confirm(
              "Email subject must not be empty!",
              () => {
                setLoading(false);
              },
              () => {
                setLoading(false);
              }
            );
          }
        }
      }
    }, nonstate.waitForAttachmentUploadInterval);
  };

  //watch for databaseResultAction
  useEffect(() => {
      if (
          appContext.databaseActionModeQueue.length > 0
          && appContext.databaseResultAction != null
      ) {
          const ExecuteDatabaseResultAction = async () => {
              const [res, mode] = await appContext.databaseResultAction();

              if (!JSON.stringify(res).includes('_false')) {
                  switch (mode) {
                      case 'updateFooterSignature':
                        break;
                  }
              } else {
                  console.log('ExecuteDatabaseResultActionErr: ' + mode + ' ' + res);
              }
          }

          switch (appContext.databaseActionModeQueue[0]) {
              case "updateFooterSignature":
                  ExecuteDatabaseResultAction();
                  break;
          }
      }
  }, [appContext.databaseActionModeQueue, appContext.databaseResultAction]);

  // const onSelectChange = (
  //   newSelectedRowKeys: React.Key[],
  //   rows: DataType[]
  // ) => {
  //   setSelectedRowKeys(newSelectedRowKeys);

  //   setSelectedRows(rows);
  // };

  // const rowSelection: TableRowSelection<DataType> = {
  //   selectedRowKeys,
  //   onChange: onSelectChange,
  // };
  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    selectedRows: DataType[]
  ) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(selectedRows);
  };

  const rowSelection: TableRowSelection<DataType> = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys, rows) => {
      setSelectedRowKeys(newSelectedRowKeys);
      setSelectedRows(rows);
    },
    onSelectAll: (selected) => {
      if (selected) {
        const allKeys = allRows.map((item) => item.key);
        setSelectedRowKeys(allKeys);
        setSelectedRows(allRows);
      } else {
        setSelectedRowKeys([]);
        setSelectedRows([]);
      }
    },
    getCheckboxProps: (record) => ({
      disabled: false, // Ensure no row is disabled
    }),
  };

  const hasSelected = selectedRowKeys.length > 0;

  return (
    <>
      {contextHolder}
      {contextHolderModal}

      <Flex vertical gap="middle">
        <Flex align="center" justify="space-between" style={{ width: "100%" }}>
          <Flex align="center" gap="middle">
            <Button
              type="primary"
              onClick={() => handleClickBlast(userID)}
              disabled={!hasSelected || !emailContent}
              loading={loading}
            >
              Blast
            </Button>
            {hasSelected ? `Selected ${selectedRowKeys.length} items` : null}
          </Flex>
        </Flex>
        <Table<DataType>
          rowSelection={rowSelection}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: rows.length }} // Ensures all rows are displayed
        />
        <Flex align="center" justify="space-between" style={{ width: "100%" }}>
          <Flex align="center" gap="middle">
            <Checker label='include Android App Link' checked={shouldAddAndroidLinkFooter} SetChecked={SetShouldAddAndroidLinkFooter}/>
            <Checker label='include IOS App Link' checked={shouldAddIosLinkFooter} SetChecked={SetShouldAddIosLinkFooter}/>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default App;
